import React, { useMemo, useCallback, useRef, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getCurrentLanguage } from '@/i18next'
import { Error } from '@/ui'
import styles from './style.module.scss'
import { VariableSizeList as List } from 'react-window'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
const ITEM_HEIGHT_IN_PX = 42

const Option = ({
	code,
	id,
	label,
	onSelect,
	style,
	setSize,
	disabled,
	activeParents,
	setActiveParents,
	searchQuery = '',
	isParentWithoutChildren = false,
}) => {
	const ref = useRef()
	useEffect(() => {
		// Проверяем, что ref.current существует и изменился размер
		if (ref.current) {
			const height = ref.current.offsetHeight + 24
			// Вызываем setSize только если высота действительно изменилась
			setSize(height)
		}
	}, []) // Зависимость пустая - эффект выполнится только при монтировании

	// Проверяем, активен ли родительский элемент
	const isActive = activeParents.includes(id)

	// Обработчик клика по элементу списка
	const handleClick = useCallback(() => {
		// Если это родительский элемент без потомков, обрабатываем его как дочерний
		if (disabled && isParentWithoutChildren) {
			onSelect(code)
			return
		}

		// Если это обычный родительский элемент
		if (disabled) {
			// Переключаем состояние активности для родителя
			setActiveParents((prev) => {
				// Если родитель уже активен - удаляем его из списка
				if (prev.includes(id)) {
					return prev.filter((parentId) => parentId !== id)
				}
				// Иначе добавляем в список активных
				else {
					return [...prev, id]
				}
			})
			return
		}
		// Для дочерних элементов вызываем функцию выбора
		onSelect(code)
	}, [code, onSelect, disabled, id, setActiveParents, isParentWithoutChildren])

	// Подсветка текста поиска с поддержкой нескольких совпадений
	const highlightText = useCallback((text, query) => {
		if (!query || !text) return text

		const searchLower = query.toLowerCase()
		const textLower = text.toLowerCase()

		if (!textLower.includes(searchLower)) return text

		// Разделяем текст на части и подсвечиваем все вхождения
		let lastIndex = 0
		let index = textLower.indexOf(searchLower)
		const parts = []

		while (index !== -1) {
			// Добавляем текст до совпадения
			if (index > lastIndex) {
				parts.push(text.substring(lastIndex, index))
			}

			// Добавляем подсвеченное совпадение
			parts.push(
				<span key={index} className={styles.highlight}>
					{text.substring(index, index + query.length)}
				</span>
			)

			lastIndex = index + query.length
			index = textLower.indexOf(searchLower, lastIndex)
		}

		// Добавляем оставшийся текст после последнего совпадения
		if (lastIndex < text.length) {
			parts.push(text.substring(lastIndex))
		}

		return <>{parts}</>
	}, [])

	return (
		<li className={`${styles.option}`} onClick={handleClick} style={style}>
			<div className={disabled && !isParentWithoutChildren ? styles.icon__wrapper : styles.icon__wrapper__child}>
				{disabled &&
					!isParentWithoutChildren &&
					(isActive ? <KeyboardArrowUpIcon fontSize={'large'} /> : <KeyboardArrowDownIcon fontSize={'large'} />)}
			</div>
			<label className={disabled && !isParentWithoutChildren ? styles.parent__label : ''} ref={ref}>
				{highlightText(label, searchQuery)}
			</label>
		</li>
	)
}

export const Options = ({
	options, // Уже отфильтрованные опции из SelectTree
	onSelect,
	showItems = 5,
	onBlur,
	optionsStyles = {},
	hideNotFoundError,
	searchQuery = '',
	expandedParents = [],
	parentsWithoutChildren = [],
}) => {
	// Используем массив активных элементов вместо одного
	const [activeParents, setActiveParents] = useState([])
	const { t } = useTranslation()
	const currentLanguage = getCurrentLanguage()
	const lang = useMemo(() => (currentLanguage === 'rus' ? 'nameRu' : 'nameKz'), [currentLanguage])

	// Автоматическое раскрытие всех нужных родительских узлов на основе поиска
	useEffect(() => {
		// При наличии поискового запроса и найденных родителей
		if (searchQuery && expandedParents.length > 0) {
			// Устанавливаем все найденные родительские элементы как активные
			setActiveParents(expandedParents)
		} else if (!searchQuery && expandedParents.length === 0) {
			// При очистке поиска сохраняем текущие раскрытые элементы,
			// чтобы пользователи могли продолжать работу с раскрытым деревом
		}
	}, [searchQuery, expandedParents])

	// Формируем плоский список элементов для отображения
	// Создаем кэш для предотвращения лишних вычислений
	const optionsMapRef = useRef(new Map())

	// Заполняем кэш при изменении options
	useEffect(() => {
		const newMap = new Map()
		options.forEach((option) => {
			// Группируем дочерние элементы по parentId
			if (option.parentId) {
				if (!newMap.has(option.parentId)) {
					newMap.set(option.parentId, [])
				}
				newMap.get(option.parentId).push(option)
			}
		})
		optionsMapRef.current = newMap
	}, [options])

	const finalOptions = useMemo(() => {
		// Получаем всех родителей
		const parentOptions = options.filter((option) => option.parentId === null)

		// Создаем результирующий массив
		let result = []

		// Для каждого родителя проверяем, нужно ли показывать его дочерние элементы
		parentOptions.forEach((parent) => {
			// Добавляем родителя в список
			result.push(parent)

			// Проверяем, есть ли у родителя дочерние элементы
			const isParentWithChildren = !parentsWithoutChildren.includes(parent.id)

			// Если родитель активен и имеет дочерние элементы, добавляем его дочерние элементы
			if (activeParents.includes(parent.id) && isParentWithChildren) {
				// Используем кэшированные дочерние элементы вместо фильтрации
				const children = optionsMapRef.current.get(parent.id) || []
				result = [...result, ...children]
			}
		})

		return result
	}, [options, activeParents, parentsWithoutChildren])

	const isOptionsLessThenShowItems = finalOptions.length < showItems
	const listHeight = useMemo(
		() => (!isOptionsLessThenShowItems ? showItems : finalOptions.length) * ITEM_HEIGHT_IN_PX,
		[isOptionsLessThenShowItems, finalOptions.length, showItems]
	)

	const listRef = useRef()
	const sizeMap = useRef({})

	const setSize = useCallback((index, size) => {
		// Обновляем размер в кэше только если он изменился
		if (sizeMap.current[index] !== size) {
			sizeMap.current[index] = size
			// Проверяем наличие ссылки перед вызовом метода
			if (listRef.current) {
				listRef.current.resetAfterIndex(index)
			}
		}
	}, [])

	// Мемоизируем функцию рендеринга строки для предотвращения лишних рендеров
	const renderRow = useCallback(
		({ index, style }) => {
			const { id, code, icon, parentId, fndChild, ...option } = finalOptions[index]
			// Проверяем, является ли этот родитель родителем без потомков
			const isParentWithoutChildren = parentId === null && parentsWithoutChildren.includes(id)
			const label = option[currentLanguage] || option[lang]

			return (
				<React.Fragment key={`option-${id}`}>
					<Option
						code={code}
						id={id}
						label={label}
						onSelect={onSelect}
						style={style}
						setSize={(size) => setSize(index, size)}
						disabled={parentId === null}
						activeParents={activeParents}
						setActiveParents={setActiveParents}
						searchQuery={searchQuery}
						isParentWithoutChildren={isParentWithoutChildren}
					/>
				</React.Fragment>
			)
		},
		[
			finalOptions,
			currentLanguage,
			lang,
			onSelect,
			setSize,
			activeParents,
			setActiveParents,
			searchQuery,
			parentsWithoutChildren,
		]
	)

	const getSize = (index) => sizeMap.current[index] || ITEM_HEIGHT_IN_PX

	if (finalOptions.length === 0) {
		return (
			<div className={styles.options} style={optionsStyles}>
				{!hideNotFoundError && <Error className={styles.notFound}>{t('no_data')}</Error>}
			</div>
		)
	}

	return (
		<ul
			className={`${!isOptionsLessThenShowItems ? ` scroll` : ''} ${styles.options}`}
			style={{ height: listHeight, ...optionsStyles, paddingLeft: 0 }}
			onBlur={onBlur}
		>
			<List className="scroll" ref={listRef} height={listHeight} itemCount={finalOptions.length} itemSize={getSize}>
				{renderRow}
			</List>
		</ul>
	)
}
